import { useState, useEffect } from '@wordpress/element';

/**
 * Custom hook to detect media queries.
 * 
 * @param {string} query - Media query string (e.g., '(max-width: 768px)')
 * @returns {boolean} Whether the media query matches
 */
export function useMediaQuery(query) {
  const [matches, setMatches] = useState(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia(query);
    
    // Set initial value
    setMatches(mediaQuery.matches);

    // Create event listener
    const handleChange = (e) => setMatches(e.matches);
    
    // Modern browsers
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    }
    // Fallback for older browsers
    else {
      mediaQuery.addListener(handleChange);
      return () => mediaQuery.removeListener(handleChange);
    }
  }, [query]);

  return matches;
}

/**
 * Predefined breakpoints hook for common use cases.
 * Matches WordPress/mobile-first approach.
 */
export function useBreakpoints() {
  const isMobile = useMediaQuery('(max-width: 767px)');
  const isTablet = useMediaQuery('(min-width: 768px) and (max-width: 1023px)');
  const isDesktop = useMediaQuery('(min-width: 1024px)');
  const isMobileOrTablet = useMediaQuery('(max-width: 1023px)');

  return {
    isMobile,
    isTablet,
    isDesktop,
    isMobileOrTablet,
  };
}

/**
 * Simple mobile detection (768px breakpoint).
 * Most common use case.
 */
export function useIsMobile() {
  return useMediaQuery('(max-width: 767px)');
}

